import React from "react";
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid
} from "@mui/material";
import { PageLayout } from "../components/page-layout";

function Home() {
  return (
    <PageLayout>
    <Container maxWidth="lg" sx={{ textAlign: "center", padding: "40px 20px" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0d0d0d, #232323)",
          color: "white",
          padding: "60px 30px",
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          marginBottom: "40px"
        }}
      >
        <Typography variant="h2" fontWeight={700} gutterBottom>
          Marble Stocks
        </Typography>
        <Typography variant="h5" sx={{ maxWidth: "700px", margin: "auto", opacity: 0.85 }}>
          Smarter Investing for Beginners — Understand What Truly Drives Stock Prices
        </Typography>
      </Box>

      {/* Price vs Profit Section */}
      <Box sx={{ marginTop: "40px", maxWidth: "900px", marginX: "auto" }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Price vs Profit: The Most Important Chart for New Investors
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Stock prices can move for many short-term reasons — news, hype, fear, speculation — but in the long run,
          <strong>profits are what define value</strong>. A company that consistently grows its net income becomes more valuable,
          and eventually, the stock price follows.
          <br /><br />
          By plotting <strong>Stock Price</strong> and <strong>Net Income (Profit)</strong> together, you can immediately see:
        </Typography>

        <Grid container spacing={3} sx={{ marginTop: "20px" }} alignItems="stretch" justifyContent="center">
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6">Long‑Term Correlation</Typography>
                <Typography variant="body2" color="text.secondary">
                  When net income grows consistently, stock prices tend to follow. The stronger the relationship, the safer the investment.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6">Spot Overvaluation</Typography>
                <Typography variant="body2" color="text.secondary">
                  If price rises but profits don’t, the stock may be overpriced. This chart helps detect bubbles early.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6">Find Hidden Opportunities</Typography>
                <Typography variant="body2" color="text.secondary">
                  When profits rise faster than the stock price, you may have found an undervalued company.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Core Criteria Section */}
      <Box sx={{ marginTop: "60px" }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Our Core Criteria for Strong, Reliable Companies
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "850px", margin: "auto", marginBottom: "30px", lineHeight: 1.7 }}>
          These filters help beginners avoid risky companies and focus on stable, long‑term performers.
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: "12px", padding: "5px" }}>
              <CardContent>
                <Typography variant="h6">10 Years of Profits</Typography>
                <Typography variant="body2" color="text.secondary">
                  A decade of positive net income shows resilience through market cycles.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: "12px", padding: "5px" }}>
              <CardContent>
                <Typography variant="h6">Debt / EBITDA ≤ 2x</Typography>
                <Typography variant="body2" color="text.secondary">
                  Lower debt means lower risk and healthier long‑term performance.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: "12px", padding: "5px" }}>
              <CardContent>
                <Typography variant="h6">Profit Margin ≥ 5%</Typography>
                <Typography variant="body2" color="text.secondary">
                  A solid profit margin shows operational efficiency and pricing power.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: "12px", padding: "5px" }}>
              <CardContent>
                <Typography variant="h6">Cash ≥ Short‑Term Debt</Typography>
                <Typography variant="body2" color="text.secondary">
                  Healthy liquidity protects the company during unexpected downturns.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box sx={{ marginTop: "60px" }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Explore Real Stocks With Real Data
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: "700px", margin: "auto", marginBottom: "20px", lineHeight: 1.7 }}
        >
          Browse US and ASX companies, visualize Price vs. Profit charts, measure correlation, spot long‑term trends,
          and understand what truly drives stock performance.
        </Typography>

        <Button
          variant="contained"
          color="primary"
          sx={{ margin: "10px", padding: "12px 24px", fontSize: "16px", borderRadius: "10px" }}
          component={Link}
          to="/usa"
        >
          View US Stocks
        </Button>

        <Button
          variant="contained"
          color="secondary"
          sx={{ margin: "10px", padding: "12px 24px", fontSize: "16px", borderRadius: "10px" }}
          component={Link}
          to="/asx"
        >
          View ASX Stocks
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={{ marginTop: "80px", color: "gray" }}>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          © 2025 Marble Stocks — Educational Only, Not Financial Advice.
        </Typography>
      </Box>
    </Container>
    </PageLayout>
  );
}

export default Home;
